import { $, gsap } from "../shared/globals.js";
import { getSceneImageClass } from "../utils/getSceneImageClass.js";

export function createHeroScrollParallaxFeature({ context, prefersReducedMotion, mm }) {
    const { $mainScene, $backgroundImgs, sceneImageClasses } = context;

    let heroScrollTimeline = null;
    const MOBILE_OFFSET_MULTIPLIER = 0.45;
    const DESKTOP_SCRUB = 1.05;
    const MOBILE_SCRUB = 0.8;

    const heroOffsets = {
        "img-f-01": { xPercent: -4.2, yPercent: -10.5 },
        "img-f-03": { xPercent: 3.1, yPercent: -6.8 },
        "img-f-04": { xPercent: 4.1, yPercent: 7.4 },
        "img-f-05": { xPercent: -3.2, yPercent: 8.9 },
        "img-s-01": { xPercent: -4.2, yPercent: -10.5 },
        "img-s-03": { xPercent: 3.1, yPercent: -6.8 },
        "img-s-04": { xPercent: 4.1, yPercent: 7.4 },
        "img-s-05": { xPercent: -3.2, yPercent: 8.9 },
        "img-s-06": { xPercent: -2.1, yPercent: 6.7 },
    };

    function getFallbackOffset($img, index) {
        const mouseIntensity = Math.max(0.75, Math.abs(parseFloat($img.data("mouse")) || 1));
        const direction = index % 2 === 0 ? -1 : 1;

        return {
            xPercent: direction * Math.min(mouseIntensity * 2.2, 4.5),
            yPercent: direction * Math.min(mouseIntensity * 5.4, 9),
        };
    }

    function clearOffsets() {
        if (!$backgroundImgs.length) return;
        gsap.set($backgroundImgs, { clearProps: "xPercent,yPercent" });
    }

    function destroyTimeline() {
        if (!heroScrollTimeline) return;

        heroScrollTimeline.scrollTrigger?.kill();
        heroScrollTimeline.kill();
        heroScrollTimeline = null;
    }

    function getScaledOffset(config, multiplier) {
        return {
            xPercent: config.xPercent * multiplier,
            yPercent: config.yPercent * multiplier,
        };
    }

    function initParallax({ offsetMultiplier = 1, scrub = DESKTOP_SCRUB } = {}) {
        destroyTimeline();

        if (prefersReducedMotion || !$mainScene.length || !$backgroundImgs.length) {
            clearOffsets();
            return;
        }

        heroScrollTimeline = gsap.timeline({
            defaults: {
                ease: "none",
            },
            scrollTrigger: {
                trigger: $mainScene[0],
                start: "top top",
                end: "bottom top",
                scrub,
                invalidateOnRefresh: true,
            },
        });

        $backgroundImgs.each(function(index) {
            const $img = $(this);
            const imgClass = getSceneImageClass($img, sceneImageClasses);
            const baseConfig = heroOffsets[imgClass] || getFallbackOffset($img, index);
            const config = getScaledOffset(baseConfig, offsetMultiplier);

            gsap.set(this, { xPercent: 0, yPercent: 0 });
            heroScrollTimeline.to(
                this,
                {
                    xPercent: config.xPercent,
                    yPercent: config.yPercent,
                },
                0
            );
        });
    }

    function register() {
        mm.add("(min-width: 768px)", () => {
            initParallax({ offsetMultiplier: 1, scrub: DESKTOP_SCRUB });

            return () => {
                destroyTimeline();
                clearOffsets();
            };
        });

        mm.add("(max-width: 767px)", () => {
            initParallax({ offsetMultiplier: MOBILE_OFFSET_MULTIPLIER, scrub: MOBILE_SCRUB });

            return () => {
                destroyTimeline();
                clearOffsets();
            };
        });
    }

    function destroy() {
        destroyTimeline();
        clearOffsets();
    }

    return {
        register,
        destroy,
    };
}
